<script setup lang="ts">
  import { ref, watch } from 'vue'
  import type { Component } from 'vue'
  import { Button } from 'ant-design-vue'
  import { CloseOutlined, QuestionCircleOutlined } from '@ant-design/icons-vue'
  import { CalculationHelpContentType } from '../models/CalculationHelpContentType'
  import { CalculationHelpContentFactory } from '../services/CalculationHelpContentFactory'
  import Spinner from '@/shared/components/Spinner.vue'

  interface ICalculationHelpPopoverProps {
    contentType: CalculationHelpContentType | string
    title?: string
  }

  const props = defineProps<ICalculationHelpPopoverProps>()

  const contentFactory = new CalculationHelpContentFactory()

  const isPopoverOpen = ref(false)
  const isLoading = ref(false)
  const resolvedComponent = ref<Component | null>(null)

  watch(
    () => props.contentType,
    async (newType) => {
      if (newType) {
        isLoading.value = true
        resolvedComponent.value = await contentFactory.getHelpContentAsync(newType)
        isLoading.value = false
      } else {
        resolvedComponent.value = null
      }
    },
    { immediate: true }
  )

  const hoverDelay = 750
  let hoverTimer: ReturnType<typeof setTimeout> | null = null

  const onMouseEnter = () => {
    hoverTimer = setTimeout(() => {
      handlePopoverChange(true)
    }, hoverDelay)
  }

  const onMouseLeave = () => {
    if (hoverTimer) {
      clearTimeout(hoverTimer)
      hoverTimer = null
    }
  }

  const handlePopoverChange = (val: boolean) => {
    isPopoverOpen.value = val
  }
</script>

<template>
  <div>
    <a-popover
      trigger="click"
      :open="isPopoverOpen"
      placement="leftTop"
      @openChange="handlePopoverChange"
      :autoAdjustOverflow="false"
    >
      <!-- 👇 Custom trigger slot -->
      <template #title>
        <div class="flex w-full items-center justify-between">
          <span v-if="!isLoading">{{ props.title ?? 'Help' }}</span>
          <CloseOutlined @click="handlePopoverChange(false)" />
        </div>
      </template>

      <template #content>
        <Spinner v-if="isLoading" message="Loading help content..." :isLoading="isLoading" />
        <component :is="resolvedComponent" v-else-if="resolvedComponent" />
        <p v-else>No help content available for this type.</p>
      </template>

      <template #default>
        <Button
          type="menu"
          class="help-button"
          @mouseenter="onMouseEnter"
          @mouseleave="onMouseLeave"
        >
          <QuestionCircleOutlined class="h-4 w-4" />
        </Button>
      </template>
    </a-popover>
  </div>
</template>

<style scoped>
  .help-button:hover {
    cursor: help;
  }
</style>
